import { Icons, Images } from "assets";
import classNames from "classnames";
import CustomTable from "shared/components/common/customTable";
import styles from "./style.module.scss";
import CustomSearch from "shared/components/common/customSearch";
import CustomPagination from "shared/components/common/customPagination";
import { useEffect, useState } from "react";
import ViewCustomerModal from "shared/modal/viewCustomerModal";
import CustomToolTip from "shared/components/common/customToolTip";
import ConfirmationModal from "shared/modal/confirmation";
import SkeletonLoader from "shared/loaders/skeleton";
import { GetAllSuppliersService } from "shared/services/suppliers";
import CustomBadge from "shared/components/common/customBadge";
import { toastMessage } from "shared/components/common/toast";
import useDebounce from "shared/hooks/useDebounce";
import { ActivateUserService, BlockUserService } from "shared/services/users";

interface Service {
  name: string;
}

interface SupplierData {
  user_id: number;
  services: Service[];
  certificates: [];
  avatar: {
    full_path: string;
  };
  website_url: string;
  bio: string;
  service_areas: string[];
  user: {
    id: number;
    fullname: string;
    phone_number: string;
    address: string;
    postal_code: string;
    city: string;
    status: number;
    auth: {
      email: string;
    };
  };
}

const DashboardSuppliers = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    supplier: SupplierData | null;
  }>({
    open: false,
    action: null,
    supplier: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [suppliers, setSuppliers] = useState<SupplierData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedSupplier, setSelectedCustomer] = useState<SupplierData | null>(
    null
  );
  const [actionType, setActionType] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");

  const openViewModal = (supplier: SupplierData) => {
    setSelectedCustomer(supplier);
    setViewModal(true);

    const action = supplier.user.status === 0 ? "Block" : "Activate";
    setActionType(action);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedCustomer(null);
    setActionType("");
  };

  const openConfirmModalFromViewModal = () => {
    if (!selectedSupplier) return;
    setConfirmModal({
      open: true,
      action: actionType,
      supplier: selectedSupplier,
    });
  };

  const openConfirmModal = (action: string, supplier: SupplierData) => {
    setConfirmModal({ open: true, action, supplier });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, supplier: null });
  };

  const handleConfirmAction = async (
    action: string,
    customer: SupplierData
  ) => {
    const {
      user: { id },
    } = customer;
    setActionLoading(true);
    try {
      if (action === "Activate") {
        const response = await ActivateUserService(id);
        if (response?.status === 200) {
          toastMessage("success", "Supplier activated successfully");
          // Update status
          setSuppliers((prevSuppliers) =>
            prevSuppliers.map((cust) =>
              cust.user?.id === id
                ? { ...cust, user: { ...cust.user, status: 1 } }
                : cust
            )
          );
        }
      } else if (action === "Block") {
        const response = await BlockUserService(id);
        if (response?.status === 200) {
          toastMessage("success", "Supplier blocked successfully");
          // Update status
          setSuppliers((prevSuppliers) =>
            prevSuppliers.map((cust) =>
              cust.user?.id === id
                ? { ...cust, user: { ...cust.user, status: 2 } }
                : cust
            )
          );
        }
      }
    } catch (error) {
      toastMessage("error", `Error: Could not ${action.toLowerCase()} user`);
      console.error(`Error ${action.toLowerCase()} user:`, error);
    } finally {
      setActionLoading(false);
      closeConfirmModal();
      closeViewModal();
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleGetAllSuppliers(page);
  };

  const customerTableHeads = [
    { title: "Avatar", key: "avatar" },
    { title: "Supplier Name", key: "supplier" },
    { title: "Phone Number", key: "phone_number" },
    { title: "Address", key: "address" },
    { title: "City", key: "city" },
    { title: "Status", key: "status" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllSuppliers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllSuppliersService(page, 10, searchValue);
      const SupplierData = Array.isArray(response.data.data.data)
        ? response.data.data.data
        : [];
      setSuppliers(SupplierData);
      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllSuppliers();
    // eslint-disable-next-line
  }, [searchValue]);

  useDebounce(
    () => {
      setSearchValue(search);
    },
    [search],
    800
  );

  const truncateText = (text: string, length: number) =>
    text.length > length ? `${text.substring(0, length)}...` : text;

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.tableSearchHeader}>
        <div className="flex justify-end">
          <CustomSearch
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
          />
        </div>
      </div>

      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={customerTableHeads}
          title="Supplier List"
          isEmpty={!suppliers.length}
          loading={loading}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
              <TableRowLoader key={index} />
            ))
            : suppliers.map((supplier) => {
              const statusText =
                supplier.user.status === 0
                  ? "Pending"
                  : supplier.user.status === 1
                    ? "Active"
                    : "Block";

              return (
                <tr key={supplier.user?.id} className="h-full">
                  <td className="flex justify-center items-center min-h-full">
                    <img
                      src={supplier.avatar?.full_path || Images.DefaultAvatar}
                      alt=""
                      className={classNames(styles.avatar)}
                    />
                  </td>
                  <td>{truncateText(supplier.user.fullname, 20)}</td>
                  <td>{supplier.user.phone_number}</td>
                  <td>{supplier.user.address}</td>
                  <td>{supplier.user.city}</td>
                  <td>
                    <CustomBadge
                      textColor={
                        statusText === "Active" ? "#027A48" : "#ED1C24"
                      }
                      bg={statusText === "Active" ? "#ecfdf3" : "#ed1c240f"}
                      title={statusText}
                    />
                  </td>
                  <td className={classNames(styles.iconContainer)}>
                    {supplier.user.status === 0 && (
                      <CustomToolTip label="Activate">
                        <button
                          onClick={() =>
                            openConfirmModal("Activate", supplier)
                          }
                        >
                          <Icons.CheckFilledLight />
                        </button>
                      </CustomToolTip>
                    )}
                    {supplier.user.status === 1 && (
                      <CustomToolTip label="Block">
                        <button
                          onClick={() => openConfirmModal("Block", supplier)}
                        >
                          <Icons.Block />
                        </button>
                      </CustomToolTip>
                    )}
                    {supplier.user.status === 2 && (
                      <CustomToolTip label="Activate">
                        <button
                          onClick={() =>
                            openConfirmModal("Activate", supplier)
                          }
                        >
                          <Icons.CheckFilledLight />
                        </button>
                      </CustomToolTip>
                    )}
                    <CustomToolTip label="Details">
                      <button onClick={() => openViewModal(supplier)}>
                        <Icons.Eye />
                      </button>
                    </CustomToolTip>
                  </td>
                </tr>
              );
            })}
        </CustomTable>
      </div>

      <div className={classNames(styles.paginationContainer, "mt-3")}>
        <CustomPagination
          onPageChange={handlePageChange}
          totalCount={totalItems}
          currentPage={currentPage}
          pageSize={10}
        />
      </div>

      {selectedSupplier && (
        <ViewCustomerModal
          title="Supplier Details"
          show={viewModal}
          handleClose={closeViewModal}
          isHaveServices
          listingDetails={{
            name: selectedSupplier.user.fullname,
            bio: selectedSupplier.bio,
            phone: selectedSupplier.user.phone_number,
            address: selectedSupplier.user.address,
            postal_code: selectedSupplier.user.postal_code,
            city: selectedSupplier.user.city,
            profileImage: "",
            services: selectedSupplier.services,
            certificates: selectedSupplier.certificates,
            website_url: selectedSupplier.website_url,
            email: selectedSupplier.user.auth.email,
            service_areas: selectedSupplier.service_areas,
          }}
          actionButtonText={actionType}
          handleSubmit={openConfirmModalFromViewModal}
          isShowAvatar
        />
      )}

      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          title={`Confirm ${confirmModal.action}`}
          actionButtonText={`Yes, ${confirmModal.action}`}
          loading={actionLoading}
          subtitle={`Are you sure you want to ${confirmModal.action?.toLowerCase()} this supplier?`}
          handleSubmit={() =>
            confirmModal.supplier &&
            handleConfirmAction(confirmModal.action!, confirmModal.supplier)
          }
          handleClose={closeConfirmModal}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 6 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default DashboardSuppliers;
